import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Label,
  Message,
  Popup,
  Table,
} from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import { renderGroup, renderQuota } from '../../helpers/render';
import { CHANNEL_OPTIONS } from '../../constants';

// 渠道类型 -> 名称/颜色 映射（复用 CHANNEL_OPTIONS，风格同 ChannelsTable）
let type2label = undefined;
function renderType(type, t) {
  if (!type2label) {
    type2label = new Map();
    for (let i = 0; i < CHANNEL_OPTIONS.length; i++) {
      type2label[CHANNEL_OPTIONS[i].value] = CHANNEL_OPTIONS[i];
    }
    type2label[0] = { value: 0, text: t('model_overview.status_unknown'), color: 'grey' };
  }
  return (
    <Label basic color={type2label[type]?.color}>
      {type2label[type] ? type2label[type].text : type}
    </Label>
  );
}

function renderStatus(status, t) {
  switch (status) {
    case 1:
      return (
        <Label basic color='green'>
          {t('model_overview.status_enabled')}
        </Label>
      );
    case 2:
    case 3:
      return (
        <Label basic color='red'>
          {t('model_overview.status_disabled')}
        </Label>
      );
    default:
      return (
        <Label basic color='grey'>
          {t('model_overview.status_unknown')}
        </Label>
      );
  }
}

// 提供模型：默认展示前若干个，超出用 Popup 悬停查看全部
const MODEL_PREVIEW_COUNT = 6;
function renderModels(models, t) {
  const list = Array.isArray(models) ? models : [];
  if (list.length === 0) {
    return <span>-</span>;
  }
  const preview = list.slice(0, MODEL_PREVIEW_COUNT);
  const rest = list.length - preview.length;
  const chips = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '2px',
        rowGap: '4px',
      }}
    >
      {preview.map((m) => (
        <Label key={m} size='small'>
          {m}
        </Label>
      ))}
      {rest > 0 && (
        <Popup
          trigger={
            <Label size='small' color='blue' style={{ cursor: 'pointer' }}>
              +{rest}
            </Label>
          }
          hoverable
          wide='very'
          content={
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                maxWidth: '360px',
              }}
            >
              {list.map((m) => (
                <Label key={m} size='small'>
                  {m}
                </Label>
              ))}
            </div>
          }
        />
      )}
    </div>
  );
  return chips;
}

// B9 模型接入总览后台页（仅管理员）
const ModelOverview = () => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/channel/overview');
      const { success, message, data } = res.data;
      if (success) {
        setChannels(data || []);
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshBalance = async (id, name) => {
    try {
      const res = await API.get(`/api/channel/update_balance/${id}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('model_overview.balance_refresh_success', { name }));
        await loadData();
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    }
  };

  // 上游余额：支持自动获取则显示 balance + 更新时间；否则显示手填 plan_remain；都没有显 "—"
  const renderBalance = (ch) => {
    if (ch.balance_supported) {
      return (
        <div>
          <span>{ch.balance}</span>
          {ch.balance_updated_time ? (
            <div style={{ color: 'gray', fontSize: '0.85em' }}>
              {t('model_overview.updated_at')}{' '}
              {timestamp2string(ch.balance_updated_time)}
            </div>
          ) : null}
        </div>
      );
    }
    if (ch.plan_remain) {
      return <span>{ch.plan_remain}</span>;
    }
    return <span>—</span>;
  };

  const renderCell = (value) => (value ? value : '—');

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {t('model_overview.title')}
          </Card.Header>
          <Message info>{t('model_overview.description')}</Message>

          <Table basic={'very'} compact size='small'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t('model_overview.table.name')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.type')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.group')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.models')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.status')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.used_quota')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.balance')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.plan_quota')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.quota_period')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.rate_limit')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('model_overview.table.actions')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {channels.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={11} textAlign='center'>
                    {t('model_overview.empty')}
                  </Table.Cell>
                </Table.Row>
              ) : (
                channels.map((ch) => (
                  <Table.Row key={ch.id}>
                    <Table.Cell>{ch.name || '-'}</Table.Cell>
                    <Table.Cell>{renderType(ch.type, t)}</Table.Cell>
                    <Table.Cell>{renderGroup(ch.group || '')}</Table.Cell>
                    <Table.Cell>{renderModels(ch.models, t)}</Table.Cell>
                    <Table.Cell>{renderStatus(ch.status, t)}</Table.Cell>
                    <Table.Cell>{renderQuota(ch.used_quota, t)}</Table.Cell>
                    <Table.Cell>{renderBalance(ch)}</Table.Cell>
                    <Table.Cell>{renderCell(ch.plan_quota)}</Table.Cell>
                    <Table.Cell>{renderCell(ch.quota_period)}</Table.Cell>
                    <Table.Cell>{renderCell(ch.rate_limit)}</Table.Cell>
                    <Table.Cell>
                      {ch.balance_supported ? (
                        <Button
                          size='tiny'
                          onClick={() => refreshBalance(ch.id, ch.name)}
                        >
                          {t('model_overview.refresh_balance')}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>

            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan={11}>
                  <Button size='small' onClick={loadData} loading={loading}>
                    {t('model_overview.refresh')}
                  </Button>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Card.Content>
      </Card>
    </div>
  );
};

export default ModelOverview;
